import { useState, useCallback } from 'react';
import * as R from 'ramda';


/**
 * Use this to store a collection of unique local data values as an array.
 *
 * This hook is restricted to handling text data types for performance. For more complex types, use hash maps.
 *
 * @param initialData - the initial data to populate the map
 * @returns a getter for the map, a function to add an element to the map, and a function to remove an element from the
 * map
 */
export const useArray = <T extends string | number>(
  initialData: T[] = [],
): [T[], (value: T) => void, (value: T) => void] => {
  const [arr, setArr] = useState<T[]>(initialData);

  const addElement = useCallback(
    (value: T) => setArr(arr.concat(value)),
    [arr],
  );

  const removeElement = useCallback(
    (value: T) => {
      const valueIndex = arr.indexOf(value);

      if (valueIndex > -1) {
        setArr(R.remove(valueIndex, 1, arr));
      }
    },
    [arr],
  );

  return [arr, addElement, removeElement];
};
